import { useCallback, useEffect, useMemo, useState } from "react";
import CryptoJS from "crypto-js";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import { BASE_URL } from "../../../api/baseURL";
import righticon from "../../../assets/images/Chevronright.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useScreenResolution from "../../../hooks/useScreenResolution";
import {
  getClaimDetails,
  selectClaimDetails,
  selectCrmClaimsLoading,
} from "../../../redux/features/crm/crmClaimSlice";
import { getClientSource } from "../../../utils/getClientSource";
import {
  ClaimBodySkeleton,
  ClaimHeaderSkeleton,
} from "./claimComponents/claimDetailsSkeleton";
import ClaimHeader from "./claimComponents/claimHeader";
import { ClaimListCard, ClaimSection } from "./claimComponents/claimListCard";
import { useClaimMotion } from "./claimComponents/claimMotion";
import ClaimSectionTabs from "./claimComponents/claimSectionTabs";
import ClaimsStepper from "./claimComponents/claimsStepper";
import {
  describeDocument,
  describeOffer,
  describePayment,
  describePaymentType,
  formatCrmTimestamp,
  resolveActiveStepKey,
} from "./claimComponents/claimStatus";
import ClaimTimeline from "./claimComponents/claimTimeline";
import OfferResponseDialog from "./claimComponents/offerResponseDialog";
import { stepperData } from "./data";

// The case number arrives AES-encrypted in the URL and base64url-encoded on top
// of it, so it has to be put back into standard base64 before it will decrypt.
const SECRET_KEY = "i-hate-meej";

const decodeClaimId = (claimId) => {
  const base64 = (claimId || "").split("-").join("+").split("_").join("/");
  if (!base64) return "";
  try {
    return CryptoJS.AES.decrypt(base64, SECRET_KEY).toString(CryptoJS.enc.Utf8);
  } catch (e) {
    // A tampered or truncated link — treated the same as a claim that does not
    // exist, which is what the "not found" state below says.
    return "";
  }
};

const SECTIONS = [
  { key: "progress", en: "Claim progress", ar: "مسار المطالبة" },
  { key: "payments", en: "Payments", ar: "المدفوعات" },
  { key: "documents", en: "Documents", ar: "المستندات" },
  { key: "offers", en: "Offers", ar: "العروض" },
];

/**
 * Claim details.
 *
 * Layout is the one decision worth spelling out. From 1024px there is room for
 * the history and the three action lists side by side, so nothing is hidden and
 * the customer sees an outstanding document without hunting for it. Below that
 * the same four sections become tabs, because a tablet in portrait cannot hold
 * a 500px rail next to anything — which the previous version tried to do, and
 * which is why it overflowed on an iPad.
 *
 * Both layouts render exactly the same section components, so there is one
 * definition of what a payment row looks like rather than a phone copy and a
 * desktop copy drifting apart.
 */
const ClaimProgressPage = () => {
  const { claim_id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const motionPresets = useClaimMotion();
  const { desktop } = useScreenResolution();

  const caseNumber = useMemo(() => decodeClaimId(claim_id), [claim_id]);

  const claimDetails = useSelector(selectClaimDetails);
  const isClaimLoading = useSelector(selectCrmClaimsLoading);

  const [history, setHistory] = useState([]);
  const [payments, setPayments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [offers, setOffers] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [settledCaseNumber, setSettledCaseNumber] = useState(null);

  const [activeSection, setActiveSection] = useState("progress");
  const [tabDirection, setTabDirection] = useState(1);

  const [activeOffer, setActiveOffer] = useState(null);
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);

  const loadClaimHistory = useCallback(async (targetCase) => {
    setIsHistoryLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/claimcrm/GetClaimHistory`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          caseNumber: targetCase,
          Channel: getClientSource(),
        }),
      });
      if (!response.ok) {
        throw new Error(`GetClaimHistory failed with HTTP ${response.status}`);
      }
      const result = await response.json();
      // Every one of these is rendered with Array.isArray guards downstream —
      // CRM omits the key entirely rather than sending an empty array.
      setHistory(result?.ClaimHistory?.Claim_Registrations_SMS || []);
      setPayments(result?.PaymentsRequests || []);
      setDocuments(result?.ClaimHistory?.Claim_Documents || []);
      setOffers(result?.CompanyOffers || []);
    } catch (error) {
      console.error("Failed to load claim history:", error);
    } finally {
      setIsHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!caseNumber) return undefined;

    let isSubscribed = true;
    setSettledCaseNumber(null);

    const detailsPromise = dispatch(
      getClaimDetails({ caseNumber, Channel: getClientSource() })
    );
    loadClaimHistory(caseNumber);

    Promise.resolve(detailsPromise).finally(() => {
      if (isSubscribed) setSettledCaseNumber(caseNumber);
    });

    return () => {
      isSubscribed = false;
    };
  }, [caseNumber, dispatch, loadClaimHistory]);

  const activeStep = useMemo(() => {
    const key = resolveActiveStepKey(claimDetails?.Claim?.ClaimStatus);
    return stepperData.find((step) => step.key === key) || stepperData[0];
  }, [claimDetails]);

  const handleSectionChange = (key) => {
    if (key === activeSection) return;
    const from = SECTIONS.findIndex((section) => section.key === activeSection);
    const to = SECTIONS.findIndex((section) => section.key === key);
    setTabDirection(to >= from ? 1 : -1);
    setActiveSection(key);
  };

  const openPaymentLink = (link) => {
    if (link) window.location.href = link;
  };

  const openDocumentUpload = (doc) => {
    if (!doc?.Document_Name) return;
    const base64 = doc.Document_Name.split("-").join("+").split("_").join("/");
    const encrypted = CryptoJS.AES.encrypt(base64, SECRET_KEY).toString();
    navigate(
      `/motor-insurance/claim-details/historydoc/${claim_id}` +
        `?claimtype=${encodeURIComponent(encrypted)}&id=${doc.Claim_Document_GUID}`
    );
  };

  const respondToOffer = async ({ decision, desc, amount }) => {
    setIsSubmittingOffer(true);
    try {
      const response = await fetch(`${BASE_URL}/claimcrm/UpdateOffer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          caseNumber,
          offer: decision,
          desc: desc || "",
          guid: activeOffer?.id,
          // Approving keeps the insurer's figure; rejecting sends the
          // customer's counter, which the dialog has already validated.
          amount: amount ?? Number.parseFloat(activeOffer?.amount),
          Channel: getClientSource(),
        }),
      });
      if (!response.ok) {
        throw new Error(`UpdateOffer failed with HTTP ${response.status}`);
      }
      setActiveOffer(null);
      // Reload rather than patch: the decision moves the claim to a new CRM
      // status, which changes the stepper, the history and the offer list.
      navigate(0);
    } catch (error) {
      console.error("Failed to submit the offer response:", error);
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  const paymentsSection = (
    <ClaimSection
      title={translate("Payments", "المدفوعات")}
      count={payments.length}
      headingHidden={!desktop}
      emptyLabel={translate("No records found", "لم يتم العثور على سجلات")}
    >
      {payments.map((payment, index) => {
        const type = describePaymentType(payment?.Type);
        return (
          <ClaimListCard
            key={`payment-${index}`}
            status={describePayment(payment)}
            title={`${translate(type.en, type.ar)} · ${payment?.Amount} ${translate("BHD", "د.ب")}`}
            meta={formatCrmTimestamp(payment?.Created_Date)}
            onSelect={() => openPaymentLink(payment?.link)}
          />
        );
      })}
    </ClaimSection>
  );

  const documentsSection = (
    <ClaimSection
      title={translate("Documents", "المستندات")}
      count={documents.length}
      headingHidden={!desktop}
      emptyLabel={translate(
        "No documents found",
        "لم يتم العثور على مستندات"
      )}
    >
      {documents.map((doc, index) => (
        <ClaimListCard
          key={`document-${index}`}
          status={describeDocument(doc)}
          // CRM numbers each accident photo separately; the customer only needs
          // to know it is an accident photo.
          title={(doc?.Document_Name || "").replace(
            /Accident Photo \d+/g,
            "Accident Photo"
          )}
          meta={formatCrmTimestamp(doc?.createdon)}
          onSelect={() => openDocumentUpload(doc)}
        />
      ))}
    </ClaimSection>
  );

  const offersSection = (
    <ClaimSection
      title={translate("Offers", "العروض")}
      count={offers.length}
      headingHidden={!desktop}
      emptyLabel={translate("No offers yet", "لا توجد عروض بعد")}
    >
      {offers.map((offer, index) => (
        <ClaimListCard
          key={`offer-${index}`}
          status={describeOffer(offer)}
          title={`${offer?.Rejection_Reason_New_offer}, ${offer?.New_offer_Amount} ${translate("BHD", "د.ب")}`}
          meta={formatCrmTimestamp(offer?.createdon)}
          onSelect={() =>
            setActiveOffer({
              id: offer?.Rejection_Reason_New_offer_GUID,
              status: offer?.Rejection_Reason_New_offer,
              amount: offer?.New_offer_Amount,
            })
          }
        />
      ))}
    </ClaimSection>
  );

  // Not a ClaimSection: the timeline owns its own empty state, and the section
  // wrapper would swallow it by rendering the generic one instead.
  const progressSection = (
    <section className="flex flex-col gap-4">
      {desktop && (
        <h2 className="text-base font-semibold leading-6 tracking-[-0.16px] text-[#1A2024]">
          {translate("Claim progress", "مسار المطالبة")}
        </h2>
      )}
      <ClaimTimeline entries={history} />
    </section>
  );

  const sectionContent = {
    progress: progressSection,
    payments: paymentsSection,
    documents: documentsSection,
    offers: offersSection,
  };

  const sectionCounts = {
    progress: 0,
    payments: payments.length,
    documents: documents.length,
    offers: offers.length,
  };

  const claimIsLoaded =
    claimDetails != null && claimDetails?.Claim?.CaseNumber === caseNumber;

  return (
    /* Capped at the 1200px the rest of the portal uses (the claims list this
       page opens from, the funds tables, the policy grids), and left-aligned
       like that list so the two screens line up as you move between them.
       Without a cap a 1920px monitor gave the history column ~1200px of its
       own — a line of text you have to move your head to read. */
    <div className="w-[1200px] max-w-full p-4 sm:p-6">
      {/* Breadcrumb from 768px, a back button below it. Both are always in the
          DOM and swapped by CSS — this used to depend on a debounced JS
          breakpoint, so a phone briefly rendered the desktop chrome on load. */}
      <button
        type="button"
        onClick={() => navigate("/file-a-claim")}
        aria-label={t("motorInsurance.trackClaimFlow.breadcrumb.file_a_claim")}
        className="mb-4 flex size-10 items-center justify-center rounded-full bg-[#F0F3F5] text-[#00384D] transition-colors hover:bg-[#E5E9EB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A9E7] md:hidden"
      >
        <ArrowLeft className="size-5 rtl:rotate-180" aria-hidden="true" />
      </button>

      <nav aria-label="Breadcrumb" className="mb-5 hidden md:block">
        <ol className="inline-flex items-center gap-2">
          <li className="inline-flex items-center gap-2">
            <Link
              to="/file-a-claim"
              className="text-xs font-normal leading-4 text-[#6E7C87] transition-colors hover:text-[#1A2024]"
            >
              {t("motorInsurance.trackClaimFlow.breadcrumb.file_a_claim")}
            </Link>
            <img src={righticon} alt="" aria-hidden="true" className="rtl:rotate-180" />
          </li>
          <li
            aria-current="page"
            className="text-xs font-normal leading-4 text-[#1A2024]"
          >
            {t("motorInsurance.trackClaimFlow.breadcrumb.claim_details")}
          </li>
        </ol>
      </nav>

      {isClaimLoading && !claimIsLoaded && <ClaimHeaderSkeleton />}

      {claimIsLoaded ? (
        <>
          <ClaimHeader
            claimCaseNumber={caseNumber}
            claimDate={claimDetails.Claim.IntimationDateStr}
            details={claimDetails}
          />

          <motion.section
            {...motionPresets.rise}
            aria-label={translate("Claim stage", "مرحلة المطالبة")}
            className="mt-4 rounded-2xl border border-[#DDE2E4] bg-white p-4 sm:p-5"
          >
            <ClaimsStepper steps={stepperData} activeStep={activeStep} />
          </motion.section>

          {isHistoryLoading ? (
            <div className="mt-6">
              <ClaimBodySkeleton mobile={!desktop} />
            </div>
          ) : desktop ? (
            /* Two columns. The rail sticks while the history scrolls, so the
               outstanding documents stay reachable on a long claim. */
            <div className="mt-6 grid grid-cols-[minmax(0,1fr)_380px] gap-8 items-start">
              {progressSection}
              <aside className="sticky top-6 flex flex-col gap-6">
                {paymentsSection}
                {documentsSection}
                {offersSection}
              </aside>
            </div>
          ) : (
            <div className="mt-6">
              <ClaimSectionTabs
                idPrefix="claim"
                tabs={SECTIONS.map((section) => ({
                  key: section.key,
                  label: translate(section.en, section.ar),
                  count: sectionCounts[section.key],
                }))}
                activeKey={activeSection}
                onChange={handleSectionChange}
              />

              {/* `mode="wait"` so the outgoing panel is gone before the next
                  arrives — two absolutely-positioned panels crossfading would
                  fight over the page height on a phone. */}
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={activeSection}
                  {...motionPresets.panel(tabDirection)}
                  role="tabpanel"
                  id={`claim-panel-${activeSection}`}
                  aria-labelledby={`claim-tab-${activeSection}`}
                  tabIndex={0}
                  className="pt-5 focus:outline-none"
                >
                  {sectionContent[activeSection]}
                </motion.div>
              </AnimatePresence>
            </div>
          )}

          <OfferResponseDialog
            open={Boolean(activeOffer)}
            offer={activeOffer}
            onClose={() => setActiveOffer(null)}
            onRespond={respondToOffer}
            isSubmitting={isSubmittingOffer}
          />
        </>
      ) : (
        settledCaseNumber === caseNumber &&
        !isClaimLoading && (
          <div className="mt-6 rounded-2xl border border-dashed border-[#DDE2E4] bg-[#FBFCFD] px-6 py-10 text-center">
            <p className="text-base font-semibold text-[#1A2024]">
              {translate(
                "We couldn't find this claim.",
                "لم نتمكن من العثور على هذه المطالبة."
              )}
            </p>
            <p className="mx-auto mt-1 max-w-[46ch] text-sm leading-5 text-[#6E7C87]">
              {translate(
                "The link may have expired or the case number may be incorrect. Try opening the claim again from your claims list.",
                "قد يكون الرابط منتهي الصلاحية أو رقم الحالة غير صحيح. حاول فتح المطالبة مرة أخرى من قائمة مطالباتك."
              )}
            </p>
            <button
              type="button"
              onClick={() => navigate("/file-a-claim")}
              className="mt-5 inline-flex items-center rounded-xl bg-[#55C6EF] px-4 py-2.5 text-sm font-semibold text-[#1A2024] transition-colors hover:bg-[#3FBCEA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A9E7] focus-visible:ring-offset-2"
            >
              {t("motorInsurance.trackClaimFlow.breadcrumb.file_a_claim")}
            </button>
          </div>
        )
      )}
    </div>
  );
};

export default ClaimProgressPage;
